import type { RowData, Row } from '@tanstack/react-table';

import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Checks if a row is disabled based on its own state and the state of its parent rows.
 * @param cacheMap - save lazily to this map
 * @param row - row to check
 * @param isDisabledCallback - optionally for getting notified when isDisabled is called (for example, used to test performance)
 */
export function isRowDisabled<TData extends RowData>(
  cacheMap: Map<string, boolean>,
  row: Row<TDataTableFeatures, TData> | undefined,
  isDisabledCallback?: (
    row: Row<TDataTableFeatures, TData> | undefined,
  ) => void,
): boolean {
  if (row === undefined) {
    return false;
  }

  // Check cache first
  const cachedResult = cacheMap.get(row.id);
  if (cachedResult !== undefined) {
    return cachedResult;
  }

  // Only call callback when actually computing the value (not when using cache)
  isDisabledCallback?.(row);

  // Determine if disabled
  let result = false;
  if (row.getCanExpandDisabled()) {
    result = true;
  } else {
    try {
      const parent = row.getParentRow();
      if (parent !== undefined) {
        result = isRowDisabled(cacheMap, parent, isDisabledCallback);
      }
    } catch {
      // If tanstack cannot find a parent row, they now throw an error instead of returning undefined.
      // In this case, we can safely assume that the row is not disabled by a parent row.
    }
  }

  cacheMap.set(row.id, result);
  return result;
}

/**
 * Calculates the nesting depth of a given sub-row.
 * @param id - the row id.
 * @param separator - the separator used in the id to denote levels of depth (default is '.').
 */
export const depthFromId = (id: string, separator = '.'): number => {
  let depth = 0;
  for (const char of id) {
    if (char === separator) {
      depth++;
    }
  }
  return depth + 1;
};

/**
 * Calculates the maximum visible depth of the expanded rows.
 * @param rowIds - the list of expanded row ids.
 * @param separator - the separator used in the id to denote levels of depth (default is '.').
 * @returns the maximum visible depth.
 */
export function calculateMaxVisibleDepth(
  rowIds: string[],
  separator: string = '.',
): number {
  let maxDepth = 0;
  let prevLevel = 0;
  let prevId = rowIds.at(0) ?? '';

  const sortedRowIds = rowIds.toSorted();
  for (const rowId of sortedRowIds) {
    const currentLevel = depthFromId(rowId, separator);
    const sameBranch = rowId.startsWith(prevId);

    // Detected a gap in the expands
    if (sameBranch && currentLevel > prevLevel + 1) {
      continue;
    }

    // When we switch the branch, we need to reset the level in order to detect
    // gaps from the start.
    if (sameBranch === false) {
      prevLevel = 0;
    }

    // Now detect if we have skipped a level.
    if (currentLevel > prevLevel + 1) {
      continue;
    }

    prevId = rowId;
    prevLevel = currentLevel;
    maxDepth = Math.max(maxDepth, currentLevel);
  }

  return maxDepth;
}
